/*
 * From http://www.redblobgames.com/maps/mapgen2/
 * Copyright 2017 Red Blob Games <redblobgames@gmail.com>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *      http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import FlatQueue       from 'flatqueue';
import * as util       from './util';
import * as Water      from './water';
import * as Elevation  from './elevation';
import * as Rivers     from './rivers';
import * as Moisture   from './moisture';
import * as Biomes     from './biomes';
import * as NoisyEdges from './noisy-edges';

/**
 * Map generator
 *
 * Map coordinates are 0 ≤ x ≤ 1000, 0 ≤ y ≤ 1000.
 *
 * mesh: TriangleMesh
 * noisyEdgeOptions: {length, amplitude, seed}
 * makeRandInt: function(seed) -> function(N) -> an int from 0 to N-1
 */
export class WorldMap {
    /**
     * @param {TriangleMesh} mesh
     */
    constructor(mesh, noisyEdgeOptions, makeRandInt) {
        this.mesh = mesh;
        this.makeRandInt = makeRandInt;
        this.lines_s = NoisyEdges.assign_lines_s(
            [],
            this.mesh,
            noisyEdgeOptions,
            this.makeRandInt(noisyEdgeOptions.seed)
        );

        this.water_r = [];
        this.ocean_r = [];
        this.coastdistance_t = [];
        this.elevation_t = [];
        this.s_downslope_t = [];
        this.elevation_r = [];
        this.flow_s = [];
        this.waterdistance_r = [];
        this.moisture_r = [];
        this.coast_r = [];
        this.temperature_r = [];
        this.biome_r = [];
        this.farm_r = []; // null | integer, town id that owns it
        this.town_r = []; // null | integer, town id
        this.road_s = []; // boolean
        this.debug_r = []; // string | number
    }

    
    calculate(options) {
        options = Object.assign({
            noise: null, // required: function(nx, ny) -> number from -1 to +1
            shape: {round: 0.5, inflate: 0.4, amplitudes: [1/2, 1/4, 1/8, 1/16]},
            numRivers: 30,
            drainageSeed: 0,
            riverSeed: 0,
            noisyEdge: {length: 10, amplitude: 0.2, seed: 0},
            biomeBias: {north_temperature: 0, south_temperature: 0, moisture: 0},
        }, options);

        Water.assign_water_r(this.water_r, this.mesh, options.noise, options.shape);
        Water.assign_ocean_r(this.ocean_r, this.mesh, this.water_r);
        
        Elevation.assign_elevation_t(
            this.elevation_t, this.coastdistance_t, this.s_downslope_t,
            this.mesh,
            this.ocean_r, this.water_r, this.makeRandInt(options.drainageSeed)
        );
        Elevation.redistribute_elevation_t(this.elevation_t, this.mesh);
        Elevation.assign_elevation_r(this.elevation_r, this.mesh, this.elevation_t, this.ocean_r);

        this.t_spring = Rivers.find_t_spring(this.mesh, this.water_r, this.elevation_t);
        util.randomShuffle(this.t_spring, this.makeRandInt(options.riverSeed));
        
        this.t_river = this.t_spring.slice(0, options.numRivers);
        Rivers.assign_flow_s(this.flow_s, this.mesh, this.s_downslope_t, this.t_river);
        
        Moisture.assign_moisture_r(
            this.moisture_r, this.waterdistance_r,
            this.mesh,
            this.water_r, Moisture.find_moisture_r_seeds(this.mesh, this.flow_s, this.ocean_r, this.water_r)
        );
        Moisture.redistribute_moisture_r(this.moisture_r, this.mesh, this.water_r,
                                         options.biomeBias.moisture, 1 + options.biomeBias.moisture);

        Biomes.assign_coast_r(this.coast_r, this.mesh, this.ocean_r);
        Biomes.assign_temperature_r(
            this.temperature_r,
            this.mesh,
            this.elevation_r,
            options.biomeBias.north_temperature, options.biomeBias.south_temperature
        );
        Biomes.assign_biome_r(
            this.biome_r,
            this.mesh,
            this.ocean_r, this.water_r, this.coast_r, this.temperature_r, this.moisture_r
        );

        this.addTowns();
    }


    addTowns() {
        // Analyze the map to pick the next town location
        // Pick some candidates and see how good they are on various metrics

        const {mesh} = this;
        let randInt = this.makeRandInt(12345);
        this.debug_r.length = mesh.numRegions; this.debug_r.fill("");
        this.farm_r.fill(null);
        this.town_r.fill(null);
        this.road_s.fill(false);

        // We want an additional boolean: ocean that is adjacent to
        // land. This will allow for navigation in shallow seas, for
        // trade routes.
        let shallowocean_r = [];
        for (let r = 0; r < mesh.numSolidRegions; r++) {
            shallowocean_r[r] = false;
            if (!this.ocean_r[r]) continue;
            for (let r2 of mesh.r_around_r(r)) {
                if (!this.ocean_r[r2]) {
                    shallowocean_r[r] = true;
                    break;
                }
            }
        }
        
        const BIOME_MOVEMENT_COST = {
            SNOW: 15,
            SCORCHED: 15,
            TUNDRA: 9,
            TROPICAL_RAIN_FOREST: 7,
            TEMPERATE_RAIN_FOREST: 7,
            TROPICAL_SEASONAL_FOREST: 5,
            TEMPERATE_DECIDUOUS_FOREST: 5,
            SUBTROPICAL_DESERT: 5,
        };
        const LAND_MOVEMENT_COST = 4;
        const ROAD_MOVEMENT_COST = 2;
        const RIVER_MOVEMENT_COST = 1;
        const WATER_MOVEMENT_COST = 1;
        const WATER_LAND_TRANSITION_COST = 2; // TODO: maybe only allow switching boat/cart at a town
        const calculateReachableArea = (r_start) => {
            // Run Dijkstra's algorithm to some travel distance, and
            // return the movement cost to each region
            const DISTANCE_LIMIT = 15; // TODO: adjust this based on the scale of the map
            let costSoFar = new Map();

            let queue = new FlatQueue();
            queue.push(r_start, 0);
            costSoFar.set(r_start, 0);
            
            let s_out = [];
            while (queue.peek() !== undefined) {
                let distanceToCurrent = queue.peekValue();
                let r_current = queue.pop();

                for (let s of mesh.s_around_r(r_current, s_out)) {
                    let r_neighbor = mesh.r_end_s(s);
                    // These rules are made up; try different rules to get different town placement
                    if (this.ocean_r[r_neighbor] && !shallowocean_r[r_neighbor]) continue; // no moving across deep ocean
                    let movementCost = BIOME_MOVEMENT_COST[this.biome_r[r_neighbor]] ?? LAND_MOVEMENT_COST;
                    if (this.road_s[s] || this.road_s[mesh.s_opposite_s(s)]) movementCost = ROAD_MOVEMENT_COST;
                    if (this.waterdistance_r[r_current] === 0 && this.waterdistance_r[r_neighbor] === 0 && this.temperature_r[r_neighbor] > 0.2) movementCost = RIVER_MOVEMENT_COST; // exclude frozen rivers
                    if (this.water_r[r_current] && this.water_r[r_neighbor]) movementCost = WATER_MOVEMENT_COST;
                    if (this.water_r[r_current] !== this.water_r[r_neighbor]) movementCost = WATER_LAND_TRANSITION_COST;
                    let distanceToNeighbor = distanceToCurrent + movementCost;
                    if (distanceToNeighbor >= DISTANCE_LIMIT) continue;

                    if (!costSoFar.has(r_neighbor) || distanceToNeighbor < costSoFar.get(r_neighbor)) {
                        costSoFar.set(r_neighbor, distanceToNeighbor);
                        queue.push(r_neighbor, distanceToNeighbor);
                    }
                }
            }

            return costSoFar;
        };

        const NEAR_TOWN_THRESHOLD = 9;
        const ARABLE_LAND_BIOMES = new Set(['GRASSLAND', 'TEMPERATE_DECIDUOUS_FOREST']);
        const evaluateTownReach = (reachableArea) => {
            let freshWater = 0; // <= NEAR_TOWN_THRESHOLD
            let arableTiles = [];
            let nearbyTowns = 0; // <= NEAR_TOWN_THRESHOLD
            let farawayTowns = 0;

            for (let [r, distance] of reachableArea.entries()) {
                 if (this.waterdistance_r[r] === 0 && distance <= NEAR_TOWN_THRESHOLD) freshWater++;
                if (ARABLE_LAND_BIOMES.has(this.biome_r[r]) && !this.farm_r[r] && 0 < distance && distance <= NEAR_TOWN_THRESHOLD) arableTiles.push(r);
                if (this.town_r[r]) {
                    if (distance <= NEAR_TOWN_THRESHOLD) nearbyTowns++;
                    else farawayTowns++;
                }
            }
            return {
                freshWater,
                arableTiles,
                nearbyTowns,
                farawayTowns,
            };
        };

        const buildOneRoad = (r_source, r_target) => {
            // Run Dijkstra's Algorithm to find the target town, then
            // build a road from there back to the source. TODO: use A*
            // if this becomes a performance bottleneck
            let sideFrom = new Map();
            let costSoFar = new Map();

            let queue = new FlatQueue();
            queue.push(r_source, 0);
            costSoFar.set(r_source, 0);

            let s_out = [];
            while (queue.peek() !== undefined && queue.peek() !== r_target) {
                let distanceToCurrent = queue.peekValue();
                let r_current = queue.pop();

                for (let s of mesh.s_around_r(r_current, s_out)) {
                    let r_neighbor = mesh.r_end_s(s);
                    // These rules are made up; tweak to change the road building
                    if (this.water_r[r_neighbor] && !shallowocean_r[r_neighbor]) continue; // no moving across deep ocean or lakes
                    if (mesh.is_ghost_r(r_neighbor)) continue;

                    const cost = (r) => {
                        let movementCost = BIOME_MOVEMENT_COST[this.biome_r[r]] ?? LAND_MOVEMENT_COST;
                        if (this.waterdistance_r[r] === 0) movementCost = RIVER_MOVEMENT_COST; // prefer roads near rivers
                        if (shallowocean_r[r]) movementCost = WATER_MOVEMENT_COST; // ocean travel is ok, but we won't build a road there
                        if (this.water_r[r] !== this.water_r[r]) movementCost = WATER_LAND_TRANSITION_COST; // changing boat to cart is slow
                        movementCost += 3 * this.elevation_r[r]; // prefer lower elevations, as they are less rugged
                        return movementCost;
                    }
                    let movementCost = Math.max(cost(r_current), cost(r_neighbor));
                    if (this.road_s[s] || this.road_s[mesh.s_opposite_s(s)]) movementCost = 0; // strongly favor existing roads
                    movementCost += 5 * (this.flow_s[s] + this.flow_s[mesh.s_opposite_s(s)]); // avoid river crossings, as they are expensive
                    let distanceToNeighbor = distanceToCurrent + movementCost;

                    if (!costSoFar.has(r_neighbor) || distanceToNeighbor < costSoFar.get(r_neighbor)) {
                        sideFrom.set(r_neighbor, s);
                        costSoFar.set(r_neighbor, distanceToNeighbor);
                        queue.push(r_neighbor, distanceToNeighbor);
                    }
                }
            }

            // build one road by following the path back from the target town
            // to the source town; this will fail if they are on different islands
            let r = r_target;
            while (r !== r_source && r !== undefined) {
                let s = sideFrom.get(r);
                let r_next = mesh.r_begin_s(s);
                if (!this.water_r[r] || !this.water_r[r_next]) {
                    this.road_s[s] = true;
                    this.road_s[mesh.s_opposite_s(s)] = true;
                }
                r = r_next;
            }
        };

        const NUM_TOWNS = 25;
        let r_towns = [];

        for (let iteration = 0; iteration < 3 && r_towns.length < NUM_TOWNS; iteration++) {
            for (let r = 0; r < mesh.numSolidRegions; r++) {
                if (this.water_r[r]) continue;
                if (randInt(100) >= 10) continue; // consider some towns each time through; TODO: randomizing order and doing one pass would be better
                let reachableArea = calculateReachableArea(r);
                let evaluation = evaluateTownReach(reachableArea);
                // Requirements for a town:
                if (evaluation.freshWater === 0) continue; // always need fresh water nearby
                if (evaluation.arableTiles.length < 2 || evaluation.farawayTowns > 0) continue; // either local food, or can trae
                if (evaluation.nearbyTowns > 0) continue; // don't want towns too close together
                // TODO: once a town builds a farm, it shouldn't be available to future towns
                this.town_r[r] = true;
                for (let r2 of evaluation.arableTiles) {
                    this.debug_r[r2] = "🗤";
                }

                for (let r_town of r_towns) {
                    buildOneRoad(r, r_town);
                }
                r_towns.push(r);
                this.debug_r[r] = "⌂"+r_towns.length;
                if (r_towns.length >= NUM_TOWNS) break;
            }
        }
    }
}
